import React from 'react';
import SEOHead from '@/components/SEOHead';

const LAST_UPDATED = 'July 17, 2026';
const CONTACT_EMAIL = 'legal@builderslab.net';

function Section({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="text-xl font-bold text-white mb-4 pb-3 border-b border-zinc-800">{title}</h2>
      <div className="space-y-4 text-zinc-400 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-20">
      <SEOHead
        title="Terms of Use"
        description="Read BuildersLab's terms of use. The rules and guidelines governing use of our AI website builder platform and related services."
        ogUrl="https://builderslab.net/TermsOfUse"
      />
      <div className="max-w-4xl mx-auto px-6">

        {/* Header */}
        <div
         
         
         
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold uppercase tracking-wider mb-6">
            Legal
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">Terms of Use</h1>
          <p className="text-zinc-500 text-sm">Last updated: {LAST_UPDATED}</p>
          <p className="text-zinc-400 mt-4 text-base leading-relaxed max-w-2xl">
            Please read these Terms of Use carefully before accessing or using BuildersLab. By creating an
            account or using any part of our platform, you agree to be bound by these terms. If you do not
            agree, you must not use the platform.
          </p>
        </div>

        <div
         
         
         
        >

          <Section title="1. Acceptance of Terms">
            <p>
              These Terms of Use ("Terms") constitute a legally binding agreement between you ("User",
              "you", "your") and <strong className="text-white">BuildersLab Limited</strong> (RC 9689278),
              a private company limited by shares incorporated in the Federal Republic of Nigeria under the
              Companies and Allied Matters Act 2020 ("BuildersLab", "we", "us", "our"). By accessing{' '}
              <strong className="text-white">builderslab.net</strong> or any of our services, you confirm
              that you are at least 16 years of age and have the legal capacity to enter into this agreement.
            </p>
            <p>
              If you are using BuildersLab on behalf of an organisation, you represent that you have the
              authority to bind that organisation to these Terms.
            </p>
          </Section>

          <Section title="2. Description of Service">
            <p>
              BuildersLab provides an AI-powered platform that enables users to generate websites, web
              applications, and digital products from natural language prompts ("the Service"). Features
              include, but are not limited to:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'AI-driven website and app code generation',
                'Live preview and device-view simulation',
                'Dashboard management of generated projects',
                'Code export and download (paid plans)',
                'Referral and credit system',
                'Team collaboration tools (where available)',
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4">
              The Service is provided on an "as-is" and "as-available" basis. We reserve the right to
              modify, suspend, or discontinue any part of the Service at any time, with or without notice.
            </p>
          </Section>

          <Section title="3. Account Registration">
            <p>
              To access certain features you must create an account. You agree to:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Provide accurate, current, and complete information during registration.',
                'Maintain the security of your password and not share it with any third party.',
                'Notify us immediately at legal@builderslab.net if you suspect unauthorised access to your account.',
                'Accept responsibility for all activity that occurs under your account.',
                'Not create more than one account per person without our express written consent.',
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="4. Acceptable Use Policy">
            <p>
              You agree to use BuildersLab only for lawful purposes. You must NOT use the platform to:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Generate content that is illegal, defamatory, harassing, abusive, threatening, or violates any applicable law.',
                'Create phishing pages, scam websites, malware distribution sites, or any form of fraudulent web presence.',
                'Generate content that infringes on the intellectual property rights of any third party.',
                'Produce content that sexualises minors in any way.',
                'Attempt to reverse-engineer, decompile, or extract source code from our AI systems or proprietary infrastructure.',
                'Circumvent usage limits, rate limits, or credit systems through automated means or account spoofing.',
                'Scrape, crawl, or systematically extract data from the platform without express written consent.',
                'Use the platform to train a competing AI model.',
                'Share access credentials with others to circumvent subscription requirements.',
                'Interfere with or disrupt the integrity or performance of the Service.',
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4">
              Violation of this Acceptable Use Policy may result in immediate suspension or permanent
              termination of your account without refund.
            </p>
          </Section>

          <Section title="5. Intellectual Property">
            <h3 className="text-white font-semibold text-base mb-2">5.1 BuildersLab IP</h3>
            <p>
              All platform software, design, trademarks, logos, branding, and proprietary technology are
              owned by BuildersLab and protected by applicable intellectual property law. You may not
              reproduce, distribute, or create derivative works from our proprietary assets without express
              written permission.
            </p>
            <h3 className="text-white font-semibold text-base mt-5 mb-2">5.2 User-Generated Content</h3>
            <p>
              You retain full ownership of the websites and content you generate using the Service, subject
              to the following:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'You grant BuildersLab a limited, non-exclusive, royalty-free licence to store, display, and process your generated content solely to provide the Service.',
                'You warrant that your prompts and the content you generate do not infringe any third-party intellectual property rights.',
                'BuildersLab may use anonymised, non-attributable content for AI model improvement and product development.',
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <h3 className="text-white font-semibold text-base mt-5 mb-2">5.3 AI-Generated Output</h3>
            <p>
              We make no guarantees regarding the uniqueness or originality of AI-generated output. It is
              your responsibility to review generated content for third-party IP conflicts before publishing
              or commercialising it.
            </p>
          </Section>

          <Section title="6. Subscription, Credits & Payment">
            <p>
              BuildersLab operates on a credit-based model with optional paid subscription plans:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Free accounts receive a limited number of generation credits upon signup.',
                'Additional credits and premium features are available through paid subscription plans billed monthly or annually via our payment partners — Flutterwave for card, mobile money and bank transfer payments, and NOWPayments for cryptocurrency.',
                'All prices are displayed exclusive of applicable taxes (VAT, GST, etc.) which may be added at checkout.',
                'Subscriptions automatically renew at the end of each billing period unless cancelled before the renewal date.',
                'Credits are non-transferable and have no cash value.',
                'Unused credits do not roll over between billing periods on monthly plans unless otherwise stated.',
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <h3 className="text-white font-semibold text-base mt-5 mb-2">Refund Policy</h3>
            <p>
              Subscription fees are generally non-refundable. We may, at our discretion, issue a pro-rata
              refund within 7 days of an initial subscription if you have not used the Service. Refund
              requests must be submitted to{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:text-orange-300 transition-colors">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </Section>

          <Section title="7. Referral Programme">
            <p>
              BuildersLab operates a referral programme that awards credits to users who successfully refer
              new paying members. We reserve the right to:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Modify or terminate the referral programme at any time.',
                'Disqualify referrals determined to be fraudulent, self-referrals, or generated through automated means.',
                'Revoke referral credits in cases of abuse or violation of these Terms.',
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="8. Disclaimers & Limitation of Liability">
            <p>
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.',
                'BUILDERSLAB DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.',
                'WE ARE NOT RESPONSIBLE FOR THE ACCURACY, COMPLETENESS, OR SUITABILITY OF AI-GENERATED CONTENT FOR ANY PARTICULAR PURPOSE.',
                'IN NO EVENT SHALL BUILDERSLAB BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS OR DATA, ARISING FROM YOUR USE OF THE SERVICE.',
                'OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING UNDER THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU PAID TO BUILDERSLAB IN THE 12 MONTHS PRECEDING THE CLAIM.',
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span className="text-zinc-500 text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="9. Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless BuildersLab, its officers, directors,
              employees, and agents from and against any claims, liabilities, damages, losses, and expenses
              (including reasonable legal fees) arising out of or in connection with:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Your use of or access to the Service.',
                'Your violation of these Terms.',
                'Your violation of any third-party rights, including intellectual property or privacy rights.',
                'Any content you generate, publish, or transmit through the Service.',
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="10. Termination">
            <p>
              Either party may terminate the relationship under these Terms at any time:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'You may delete your account at any time from your account settings. Deletion will permanently remove your data subject to legal retention requirements.',
                'We may suspend or terminate your account immediately and without notice if you breach these Terms, engage in fraudulent activity, or if required to do so by law.',
                'Upon termination, your right to access the Service ceases immediately. We are not obligated to retain your generated content beyond your notice period.',
                'Subscription fees already paid are non-refundable upon termination for breach.',
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="11. Governing Law & Dispute Resolution">
            <p>
              These Terms are governed by and construed in accordance with the laws of the Federal
              Republic of Nigeria. Any disputes
              arising out of or relating to these Terms or the Service shall first be attempted to be
              resolved through good-faith negotiation. If unresolved within 30 days, disputes shall be
              submitted to binding arbitration.
            </p>
            <p>
              Nothing in this section prevents either party from seeking injunctive relief in a court of
              competent jurisdiction to prevent irreparable harm.
            </p>
          </Section>

          <Section title="12. Changes to These Terms">
            <p>
              We reserve the right to modify these Terms at any time. When we make material changes, we
              will notify you by email and/or a prominent platform notice at least 14 days before the
              changes take effect. Your continued use of the Service after the effective date of any updated
              Terms constitutes your acceptance of the new Terms.
            </p>
          </Section>

          <Section title="13. Contact">
            <p>
              For legal enquiries, please contact:
            </p>
            <div className="mt-4 p-5 bg-zinc-900 border border-zinc-800 rounded-xl space-y-2">
              <p className="text-white font-semibold">BuildersLab Limited (RC 9689278) — Legal Team</p>
              <p>
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-2">
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p className="text-zinc-500 text-sm">Response time: within 5 business days.</p>
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}
